import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

// --------------------------------------------------------------------------
// Automatic retry on transient failures
// --------------------------------------------------------------------------
// rooteed.com lives behind Cloudflare, and the production backend cold-starts
// after periods of inactivity. The first request after a cold-start (and any
// upstream blip mid-request) sometimes surfaces as HTTP 520 / 502 / 503 / 504
// from Cloudflare or a hard network error, even though the next attempt
// works fine. Users saw this as "Couldn't load chat history / beds / notes /
// pests…" toasts firing on what was a one-off blip.
//
// Strategy: for SAFE methods (GET/HEAD) and idempotent POSTs that explicitly
// opt in via `config.retryable: true`, retry up to RETRY_LIMIT times with
// exponential back-off. Non-idempotent writes (POST/PATCH/DELETE) without the
// opt-in flag are never retried — we don't want to accidentally double-charge
// a payment or double-create a bed.
const RETRY_LIMIT = 2;
const RETRY_BASE_DELAY_MS = 700;
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524]);

function isRetryableMethod(method, retryableFlag) {
    if (retryableFlag) return true;
    const m = (method || "get").toLowerCase();
    return m === "get" || m === "head" || m === "options";
}

async function maybeRetry(error) {
    const config = error?.config;
    if (!config || config.__retryCount === undefined) {
        if (config) config.__retryCount = 0;
    }
    const status = error?.response?.status;
    const isNetwork = !error?.response;  // axios fires this when the request never reached a server
    const isTransient = isNetwork || (status && TRANSIENT_STATUS.has(status));
    const retryable = isRetryableMethod(config?.method, config?.retryable);
    if (!isTransient || !retryable || !config) return Promise.reject(error);
    if (config.__retryCount >= RETRY_LIMIT) return Promise.reject(error);
    config.__retryCount += 1;
    const delay = RETRY_BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1);
    await new Promise((r) => setTimeout(r, delay));
    return client(config);
}

// --------------------------------------------------------------------------
// Bearer token store
// --------------------------------------------------------------------------
// We rely on httpOnly cookies as the primary auth transport, but several
// browser configurations (Safari ITP, Brave shields, third-party-cookie
// blockers, cross-origin preview URLs, Incognito sessions) silently drop
// the `access_token` cookie — which meant users would log in, get redirected
// to `/`, and then immediately bounce back to `/login` because `/auth/me`
// came back 401. Keeping a copy of the access token in `localStorage` and
// sending it as an `Authorization: Bearer` header makes login resilient to
// any of those cookie issues. Backend accepts either transport.
const TOKEN_KEY = "vm_access_token";
const memToken = { value: null };

function readToken() {
    if (memToken.value) return memToken.value;
    try {
        const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
        memToken.value = t || null;
        return memToken.value;
    } catch {
        return null;
    }
}
function writeToken(token) {
    memToken.value = token || null;
    try {
        if (typeof window === "undefined") return;
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {
        // storage denied — mem copy still works for the current tab
    }
}

client.interceptors.request.use((config) => {
    const token = readToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Paths that can legitimately 401 without us needing to redirect —
// the caller handles it locally (e.g. /auth/me on first page load).
const AUTH_OPTIONAL_PATHS = ["/auth/me", "/auth/login", "/auth/register", "/auth/refresh"];

// Public pages where a 401 is expected or irrelevant — never force a
// redirect away from them.
const PUBLIC_PATHS = ["/login", "/register", "/about", "/pricing", "/forgot-password", "/reset-password"];
const PUBLIC_PREFIXES = ["/share/", "/harvest/", "/unsubscribe/"];

// Global 401 handler — catches stale-session errors that would otherwise
// bubble as a red "Uncaught runtime error" overlay in CRA. When a protected
// endpoint returns 401 from a background effect, we quietly shove the user
// back to /login (with a return path) instead of crashing the render.
client.interceptors.response.use(
    (response) => response,
    async (error) => {
        // First chance: retry transient failures (Cloudflare 5xx + network
        // blips on GET requests). If the retry succeeds we never reach the
        // 401-handler below.
        try {
            return await maybeRetry(error);
        } catch (retried) {
            error = retried;
        }
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const pathname = typeof window !== "undefined" ? window.location.pathname : "";
        const onPublicPage = PUBLIC_PATHS.includes(pathname)
            || PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
        const isAuthOptional = AUTH_OPTIONAL_PATHS.some((p) => url.includes(p));

        if (status === 401 && !isAuthOptional && !onPublicPage && typeof window !== "undefined") {
            // Session is dead — clear any stale token we might still be
            // carrying before bouncing to login.
            writeToken(null);
            // Kick the user back to login, preserving where they were trying
            // to go so ProtectedRoute can restore it post-login.
            const current = window.location.pathname + window.location.search;
            if (!current.startsWith("/login")) {
                window.location.replace(`/login?next=${encodeURIComponent(current)}`);
            }
        }
        return Promise.reject(error);
    }
);

// Auth
export const apiRegister = (payload) =>
    client.post("/auth/register", payload).then((r) => {
        if (r.data?.access_token) writeToken(r.data.access_token);
        return r.data;
    });
export const apiLogin = (payload) =>
    client.post("/auth/login", payload).then((r) => {
        if (r.data?.access_token) writeToken(r.data.access_token);
        return r.data;
    });
export const apiLogout = () =>
    client.post("/auth/logout").then((r) => {
        writeToken(null);
        return r.data;
    }).catch((e) => {
        writeToken(null);
        throw e;
    });
export const apiMe = () => client.get("/auth/me").then(r => r.data);

// Password reset
export const requestPasswordReset = (email) =>
    client.post("/auth/forgot-password", { email }).then(r => r.data);
export const resetPassword = ({ token, new_password }) =>
    client.post("/auth/reset-password", { token, new_password }).then(r => r.data);
export const apiUpdateProfile = (payload) => client.patch("/auth/me", payload).then(r => r.data);
export const apiChangePassword = (payload) => client.post("/auth/change-password", payload).then(r => r.data);
export const apiUploadAvatar = (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/auth/me/avatar", form, { headers: { "Content-Type": "multipart/form-data" } })
        .then(r => r.data);
};
export const apiDeleteAvatar = () => client.delete("/auth/me/avatar").then(r => r.data);

// Weekly Sunday sow-reminder digest
export const apiUpdateReminderPrefs = (reminder_email_enabled) =>
    client.patch("/auth/me/reminder-prefs", { reminder_email_enabled }).then(r => r.data);
// Public unsubscribe (no auth) — call via raw axios so the 401
// interceptor doesn't bounce a not-logged-in user to /login.
export const apiUnsubscribeReminder = (token) =>
    axios.get(`${API}/unsubscribe/${encodeURIComponent(token)}`).then(r => r.data);

// Harvest Party + Season PDF
export const fetchHarvestSummary = (token) => client.get(`/public/harvest-summary/${token}`).then(r => r.data);
export const harvestCardUrl = (token) => `${API}/public/harvest-card/${token}.png`;
export const harvestShareOgUrl = (token) => `${API}/public/share/${token}/og.html`;
export const seasonReportUrl = (gardenId) => `${API}/gardens/${gardenId}/season-report.pdf`;

// Billing
export const fetchBillingPackages = () => client.get("/billing/packages").then(r => r.data);
export const fetchBillingStatus = () => client.get("/billing/status").then(r => r.data);
export const startCheckout = (packageId) =>
    client.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
    }).then(r => r.data);
export const fetchCheckoutStatus = (sessionId) =>
    client.get(`/billing/checkout/${sessionId}`).then(r => r.data);
export const fetchBillingHistory = () => client.get("/billing/history").then(r => r.data);

// PayPal
export const paypalCreateOrder = (packageId) =>
    client.post("/billing/paypal/create-order", { package_id: packageId }).then(r => r.data);
export const paypalCaptureOrder = (orderId) =>
    client.post("/billing/paypal/capture-order", { order_id: orderId }).then(r => r.data);

// Admin — community garden grants & stats
export const adminGrantCommunity = (payload) =>
    client.post("/admin/grant-community", payload).then(r => r.data);
export const adminRevokeCommunity = (payload) =>
    client.post("/admin/revoke-community", payload).then(r => r.data);
export const adminGrantPlus = (payload) =>
    client.post("/admin/grant-plus", payload).then(r => r.data);
export const adminListUsers = () => client.get("/admin/users").then(r => r.data);
export const adminStats = () => client.get("/admin/stats").then(r => r.data);
export const adminTransactions = (params = {}) =>
    client.get("/admin/transactions", { params }).then(r => r.data);
export const adminCreateUser = (payload) =>
    client.post("/admin/users", payload).then(r => r.data);
export const adminDeleteUser = (userId) =>
    client.delete(`/admin/users/${userId}`).then(r => r.data);
export const adminBulkDeleteUsers = (userIds) =>
    client.post("/admin/users/bulk-delete", { user_ids: userIds }).then(r => r.data);

// Pests & beneficial insects
export const fetchPests = () => client.get("/catalog/pests").then(r => r.data);
export const fetchPest = (slug) => client.get(`/catalog/pests/${slug}`).then(r => r.data);
export const fetchBeneficials = () => client.get("/catalog/beneficials").then(r => r.data);
export const fetchBeneficial = (slug) => client.get(`/catalog/beneficials/${slug}`).then(r => r.data);

// Images
export const uploadImage = (file, { caption, gardenId, cropId } = {}) => {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    if (gardenId) form.append("garden_id", gardenId);
    if (cropId) form.append("crop_id", cropId);
    return client.post("/images", form, { headers: { "Content-Type": "multipart/form-data" } })
        .then(r => r.data);
};
export const listImages = (gardenId) =>
    client.get("/images", { params: gardenId ? { garden_id: gardenId } : {} }).then(r => r.data);
export const getImage = (id) => client.get(`/images/${id}`).then(r => r.data);
export const updateImage = (id, payload) => client.patch(`/images/${id}`, payload).then(r => r.data);
export const deleteImage = (id) => client.delete(`/images/${id}`).then(r => r.data);

// Garden share
export const enableGardenShare = (id) => client.post(`/gardens/${id}/share`).then(r => r.data);
export const disableGardenShare = (id) => client.delete(`/gardens/${id}/share`).then(r => r.data);
export const fetchPublicGarden = (token) => client.get(`/public/gardens/${token}`).then(r => r.data);
export const fetchPublicBedOccupancy = (bedId, token) =>
    client.get(`/public/beds/${bedId}/occupancy`, { params: { share_token: token } }).then(r => r.data);

// Helper to format FastAPI errors (string | array | object) to a single string
export const formatApiError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    if (detail && typeof detail.message === "string") return detail.message;
    return String(detail);
};

// Gardens
export const listGardens = () => client.get("/gardens").then(r => r.data);
export const createGarden = (payload) => client.post("/gardens", payload).then(r => r.data);
export const updateGarden = (id, payload) => client.patch(`/gardens/${id}`, payload).then(r => r.data);
export const deleteGarden = (id) => client.delete(`/gardens/${id}`).then(r => r.data);

// Structures (shed, water tank, compost bin, etc.)
export const listStructures = (garden_id) => client.get("/structures", { params: { garden_id } }).then(r => r.data);
export const createStructure = (payload) => client.post("/structures", payload).then(r => r.data);
export const updateStructure = (id, payload) => client.patch(`/structures/${id}`, payload).then(r => r.data);
export const deleteStructure = (id) => client.delete(`/structures/${id}`).then(r => r.data);

// Garden map combined view
export const getGardenMap = (garden_id) => client.get("/garden-map", { params: { garden_id } }).then(r => r.data);

// Beds
export const listBeds = (garden_id) => client.get("/beds", { params: { garden_id } }).then(r => r.data);
export const createBed = (payload) => client.post("/beds", payload).then(r => r.data);
export const updateBed = (id, payload) => client.patch(`/beds/${id}`, payload).then(r => r.data);
export const deleteBed = (id) => client.delete(`/beds/${id}`).then(r => r.data);
export const getBedLayout = (id) => client.get(`/beds/${id}/layout`).then(r => r.data);
export const getBedOccupancy = (id) => client.get(`/beds/${id}/occupancy`).then(r => r.data);
export const batchPositionMoves = (moves) => client.post(`/crops/batch-position`, { moves }).then(r => r.data);

// Crops
export const listCrops = (garden_id, bed_id) => client.get("/crops", { params: { garden_id, bed_id } }).then(r => r.data);
export const createCrop = (payload) => client.post("/crops", payload).then(r => r.data);
export const updateCrop = (id, payload) => client.patch(`/crops/${id}`, payload).then(r => r.data);
export const placeCrop = (id, position, autoSplit = true) =>
    client.post(`/crops/${id}/place`, { position, auto_split: autoSplit }).then(r => r.data);
export const deleteCrop = (id) => client.delete(`/crops/${id}`).then(r => r.data);
export const recordHarvest = (id, payload) => client.post(`/crops/${id}/harvest`, payload).then(r => r.data);
export const getCropYield = (id) => client.get(`/crops/${id}/yield`).then(r => r.data);
export const getCropSuccession = (id) => client.get(`/crops/${id}/succession`).then(r => r.data);

// Catalog
export const listCropCatalog = () => client.get("/catalog/crops").then(r => r.data);

// Dashboard / Timeline / Weather
export const getDashboard = (garden_id) => client.get("/dashboard", { params: { garden_id } }).then(r => r.data);
export const getTimeline = (garden_id) => client.get("/timeline", { params: { garden_id } }).then(r => r.data);
export const getWeather = (lat, lon) => client.get("/weather", { params: { lat, lon } }).then(r => r.data);

// Chat
export const sendChat = (payload) => client.post("/chat", payload).then(r => r.data);
export const getChatHistory = (session_id = "default") => client.get("/chat/history", { params: { session_id } }).then(r => r.data);
export const clearChat = (session_id = "default") => client.delete("/chat/history", { params: { session_id } }).then(r => r.data);

// AI photo analysis (vision)
export const apiVisionQuota = () => client.get("/vision/quota").then(r => r.data);
export const apiVisionAnalyze = (file, { mode = "diagnose", prompt = "" } = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    if (prompt) form.append("prompt", prompt);
    return client.post("/vision/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
};
export const apiChatPhoto = (file, { sessionId = "default", prompt = "" } = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("session_id", sessionId);
    if (prompt) form.append("prompt", prompt);
    return client.post("/chat/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
};

// PayPal Subscriptions (recurring billing — 4-day trial → €6.95/mo or €59.95/yr)
export const apiSubscriptionPlans = () => client.get("/subscriptions/plans").then(r => r.data);
export const apiStartSubscription = (plan, originUrl) =>
    client.post("/subscriptions/start", { plan, origin_url: originUrl }).then(r => r.data);
export const apiConfirmSubscription = (subscription_id) =>
    client.post("/subscriptions/confirm", { subscription_id }).then(r => r.data);
export const apiSubscriptionStatus = () =>
    client.get("/subscriptions/status").then(r => r.data);
export const apiCancelSubscription = () =>
    client.post("/subscriptions/cancel").then(r => r.data);
// Testimonials
export const applyChatPlan = ({ plan, garden_id }) =>
    client.post("/ai-plans/apply", { plan, garden_id: garden_id || undefined }).then(r => r.data);
export const adminListIllustrations = () =>
    client.get("/admin/illustrations").then(r => r.data);
export const adminRegenerateIllustration = ({ crop_key, bucket }) =>
    client.post("/admin/illustrations/regenerate", { crop_key, bucket }).then(r => r.data);
export const fetchPublicTestimonials = (limit = 12) =>
    client.get("/testimonials/public", { params: { limit } }).then(r => r.data);
export const fetchMyTestimonial = () =>
    client.get("/testimonials/me").then(r => r.data);
export const submitTestimonial = (payload) =>
    client.post("/testimonials", payload).then(r => r.data);
export const deleteMyTestimonial = () =>
    client.delete("/testimonials/me").then(r => r.data);
export const adminListTestimonials = (status) =>
    client.get("/admin/testimonials", { params: status ? { status } : {} }).then(r => r.data);
export const adminUpdateTestimonial = (id, patch) =>
    client.patch(`/admin/testimonials/${id}`, patch).then(r => r.data);
export const adminDeleteTestimonial = (id) =>
    client.delete(`/admin/testimonials/${id}`).then(r => r.data);

// Book notes
export const interpretBookNotes = (payload) => client.post("/book-notes/interpret", payload).then(r => r.data);
export const importBookNoteFile = (file, source, garden_id) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/book-notes/import-file", fd, {
        params: { source: source || "", ...(garden_id ? { garden_id } : {}) },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
    }).then(r => r.data);
};

// Chunked upload — for files larger than the ingress body cap (~25 MB).
// Splits the file into ~4 MB chunks, uploads up to `concurrency` in parallel,
// finalises asynchronously (returns a job_id), and polls the job until the
// PDF extraction + LLM interpretation finishes. Sidesteps Cloudflare's 100 s
// request timeout.
export const importBookNoteChunked = async ({
    file, source = "", garden_id = null, onProgress, onStage, concurrency = 3,
}) => {
    const init = await client.post("/book-notes/chunked/init", {
        filename: file.name,
        total_bytes: file.size,
        content_type: file.type || "",
        source: source || "",
        garden_id: garden_id || null,
    }).then(r => r.data);

    const chunkSize = init.chunk_size_recommended || 4 * 1024 * 1024;
    const total = Math.max(1, Math.ceil(file.size / chunkSize));
    let sent = 0;
    let finished = 0;
    const queue = Array.from({ length: total }, (_, i) => i);

    const uploadOne = async (i) => {
        const slice = file.slice(i * chunkSize, (i + 1) * chunkSize);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(i));
        fd.append("chunk", slice, `chunk-${i}`);
        await client.post("/book-notes/chunked/append", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
        sent += slice.size;
        finished += 1;
        if (onProgress) onProgress({ sent, total: file.size, chunk: finished, chunks: total });
    };

    const worker = async () => {
        while (queue.length > 0) {
            const next = queue.shift();
            if (next === undefined) return;
            await uploadOne(next);
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(concurrency, total) }, () => worker())
    );

    // Kick off async processing — returns immediately with a job_id.
    const finalFd = new FormData();
    finalFd.append("upload_id", init.upload_id);
    const { job_id } = await client.post("/book-notes/chunked/finalize", finalFd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
    }).then(r => r.data);

    if (onStage) onStage("processing");

    // Poll every 3 s (up to ~6 min) until the job is done or errors.
    const maxAttempts = 120;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const job = await client.get(`/book-notes/jobs/${job_id}`).then(r => r.data);
        if (onStage) onStage(job.status, job.progress);
        if (job.status === "done") return job.result;
        if (job.status === "error") {
            const err = new Error(job.error || "Book note processing failed.");
            err.response = { data: { detail: job.error } };
            throw err;
        }
    }
    throw new Error("Book note processing timed out.");
};
export const importBookNoteUrl = (url, source, garden_id) =>
    client.post("/book-notes/import-url", { url, source: source || "", garden_id }, { timeout: 120000 }).then(r => r.data);
export const listBookNotes = () => client.get("/book-notes").then(r => r.data);
export const deleteBookNote = (id) => client.delete(`/book-notes/${id}`).then(r => r.data);

// CSV import
export const importCsv = (garden_id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/csv/import", fd, {
        params: { garden_id },
        headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
};

export const STAGES = [
    "planned", "sown", "germinated", "seedling", "vegetative",
    "flowering", "fruiting", "harvest", "finished",
];
export const BED_TYPES = ["raised bed", "greenhouse", "polytunnel", "container", "in-ground"];
export const SUN = ["full sun", "partial shade", "shade"];
export const SOWING_TYPES = ["direct", "transplant"];
